import { SupportRecalculationPanel, SupportPendingRecalculations, supportRecalculationKey } from '../components/recalculations/SupportRecalculationPanel';
import { ThemeToggle } from '../components/ThemeToggle';
import { TraderAppealResponse } from '../components/TraderAppealResponse';
import { bankDisplayName } from '../lib/kgsBanks';
import { formatSupportDate as formatDate, parseSupportDate } from '../lib/supportTime';
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSupportAuth } from '../contexts/SupportAuthContext';
import { supportAPI, SupportAppeal, SupportLocalStatus, AppealStatus, AppealResolution, paymentsAPI, PaymentTransactionResponse, AppealAttachment, SupportTransactionLookupItem } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Pagination } from '../components/ui/pagination';
import { LogOut, RefreshCw, FileText, ExternalLink, AlertCircle, Clock, MessageSquare, Calendar, Eye, User, Timer, Store, Users, RotateCcw, Upload, CreditCard, Phone, Building, Search, X } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Input } from '../components/ui/input';
import './SupportDashboardPage.css';

const localStatusOptions: { value: SupportLocalStatus; label: string }[] = [
    { value: 'completed', label: 'Завершён' },
    { value: 'refund_to_client', label: 'Возврат клиенту' },
    { value: 'waiting_trader', label: 'Ждем трейдера' },
    { value: 'waiting_merchant_reply', label: 'Ждем ответ мерчанта' },
    { value: 'receipt_present_waiting_another', label: 'Чек есть. Ждем другой' },
    { value: 'no_receipt_waiting', label: 'Чека нет. Ждем' },
    { value: 'waiting_client_statement', label: 'Ждем выписку клиента' },
    { value: 'waiting_trader_statement', label: 'Ждем выписку трейдера' },
];

const formatResolvedBy = (resolvedBy: string): string => {
    const mapping: Record<string, string> = {
        'trader_auto': 'Автоматически (ответ трейдера)',
        'system_auto': 'Автоматически (истечение срока)',
    };
    if (resolvedBy.startsWith('external:')) {
        const provider = resolvedBy.split(':')[1];
        return `Внешнее решение · ${provider === 'youpay' ? 'YouPay' : provider === 'flintpays' ? 'FlintPays' : provider}`;
    }
    return mapping[resolvedBy] || resolvedBy;
};

const getExternalProviderName = (appeal: SupportAppeal): string => {
    if (appeal.external_provider_name) return appeal.external_provider_name;
    if (appeal.external_provider === 'youpay') return 'YouPay';
    if (appeal.external_provider === 'flintpays') return 'FlintPays';
    if (appeal.external_provider === 'noros') return 'NOROS';
    return appeal.external_provider || 'Внешняя платформа';
};

const isExternalAppeal = (appeal: SupportAppeal): boolean =>
    Boolean(appeal.is_external || appeal.externally_managed || appeal.external_provider);

const externalStatusLabel = (status?: string): string =>
    status === 'pending_open' ? 'Ожидает открытия у провайдера' : status || 'Ожидается статус';

const getResolutionLabel = (appeal: SupportAppeal): string => {
    if (appeal.resolution_type === AppealResolution.FAVOR_MERCHANT) {
        return 'В пользу мерчанта';
    }
    if (appeal.externally_managed) {
        return `В пользу ${getExternalProviderName(appeal)}`;
    }
    return 'В пользу трейдера';
};

const getAttachmentTypeLabel = (type: AppealAttachment['type']): string => {
    const labels: Record<AppealAttachment['type'], string> = {
        initial_appeal: 'Первичный файл',
        trader_response: 'Ответ трейдера',
        reopen: 'Файл при переоткрытии',
    };
    return labels[type] || type;
};

const getAttachmentRoleLabel = (role: AppealAttachment['uploaded_by_role']): string => {
    const labels: Record<AppealAttachment['uploaded_by_role'], string> = {
        merchant: 'Мерчант',
        support: 'Саппорт',
        trader: 'Трейдер',
        system: 'Система',
    };
    return labels[role] || role;
};

const getAttachmentFilename = (attachment: AppealAttachment): string => {
    return attachment.original_filename || attachment.file_path.split('/').pop() || 'Файл';
};

const AppealTimer: React.FC<{ createdAt: string; lifetimeMinutes: number }> = ({ createdAt, lifetimeMinutes }) => {
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const interval = setInterval(() => setNow(new Date()), 60000); // Обновляем каждую минуту
        return () => clearInterval(interval);
    }, []);

    const createdDate = parseSupportDate(createdAt);
    const elapsedMs = now.getTime() - createdDate.getTime();
    const elapsedMinutes = Math.floor(elapsedMs / 60000);
    const remainingMinutes = lifetimeMinutes - elapsedMinutes;

    const formatTime = (minutes: number) => {
        if (minutes <= 0) return 'Истёк';
        const hours = Math.floor(minutes / 60);
        const mins = minutes % 60;
        if (hours > 0) {
            return `${hours}ч ${mins}м`;
        }
        return `${mins}м`;
    };

    let colorClass = 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950';
    if (remainingMinutes <= 0) {
        colorClass = 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950 font-bold';
    } else if (remainingMinutes <= 30) {
        colorClass = 'text-orange-600 dark:text-orange-300 bg-orange-50 dark:bg-orange-950';
    } else if (remainingMinutes <= 60) {
        colorClass = 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-950';
    }

    return (
        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${colorClass}`}>
            <Timer className="h-3 w-3" />
            <span>{formatTime(remainingMinutes)}</span>
        </div>
    );
};

export const SupportDashboardPage: React.FC = () => {
    const { user, logout } = useSupportAuth();
    const [appeals, setAppeals] = useState<SupportAppeal[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const [pageSize, setPageSize] = useState(50);
    const appealsRequestId = useRef(0);
    const [appealsError, setAppealsError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<string>('active');

    const [recalculationTransactionId, setRecalculationTransactionId] = useState('');
    const [appealRecalculation, setAppealRecalculation] = useState(false);
    const [confirmResolution, setConfirmResolution] = useState(false);
    const [resolutionError, setResolutionError] = useState('');
    const resolutionInFlight = useRef(false);

    // Filters
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [transactionLookupQuery, setTransactionLookupQuery] = useState('');
    const [transactionLookupResults, setTransactionLookupResults] = useState<SupportTransactionLookupItem[]>([]);
    const [isTransactionLookupLoading, setIsTransactionLookupLoading] = useState(false);
    const [transactionLookupError, setTransactionLookupError] = useState<string | null>(null);

    // Modal state
    const [selectedAppeal, setSelectedAppeal] = useState<SupportAppeal | null>(null);
    const [selectedTransaction, setSelectedTransaction] = useState<PaymentTransactionResponse | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [resolution, setResolution] = useState<AppealResolution | undefined>(undefined);
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [receiptUrl, setReceiptUrl] = useState<string | null>(null);

    // Reopen state
    const [isReopenMode, setIsReopenMode] = useState(false);
    const [reopenFiles, setReopenFiles] = useState<File[]>([]);
    const [reopenDescription, setReopenDescription] = useState('');

    const [localStatusSaving, setLocalStatusSaving] = useState<Record<string, boolean>>({});
    const [localStatusErrors, setLocalStatusErrors] = useState<Record<string, string>>({});

    const [annotationDrafts, setAnnotationDrafts] = useState<Record<string, { status: SupportLocalStatus | ''; comment: string }>>({});
    const annotationDraft = (appeal: SupportAppeal) => annotationDrafts[appeal.id] ?? {
        status: appeal.support_local_status ?? '', comment: appeal.support_local_status_comment ?? '',
    };
    const annotationDirty = (appeal: SupportAppeal) => {
        const draft = annotationDraft(appeal);
        return draft.status !== (appeal.support_local_status ?? '') || draft.comment.trim() !== (appeal.support_local_status_comment ?? '');
    };
    const handleAnnotationSave = async (appeal: SupportAppeal, statusOnly = false) => {
        const draft = annotationDraft(appeal);
        setLocalStatusSaving(previous => ({ ...previous, [appeal.id]: true }));
        setLocalStatusErrors(previous => ({ ...previous, [appeal.id]: '' }));
        try {
            // Omit the comment for inline saves so the server preserves its latest value.
            const result = statusOnly
                ? await supportAPI.updateLocalStatus(appeal.id, draft.status || null)
                : await supportAPI.updateLocalStatus(appeal.id, draft.status || null, draft.comment.trim() || null);
            setAppeals(previous => previous.map(item => item.id === appeal.id ? { ...item, ...result } : item));
            setSelectedAppeal(previous => previous?.id === appeal.id ? { ...previous, ...result } : previous);
            setAnnotationDrafts(previous => {
                const next = { ...previous };
                if (statusOnly && draft.comment.trim() !== (appeal.support_local_status_comment ?? '')) {
                    next[appeal.id] = { ...draft, status: result.support_local_status ?? '' };
                } else {
                    delete next[appeal.id];
                }
                return next;
            });
        } catch {
            setLocalStatusErrors(previous => ({ ...previous, [appeal.id]: 'Не удалось сохранить статус и комментарий. Повторите попытку.' }));
        } finally {
            setLocalStatusSaving(previous => ({ ...previous, [appeal.id]: false }));
        }
    };

    const renderLocalStatus = (appeal: SupportAppeal, compact = false) => (
        <div className="min-w-0" onClick={event => event.stopPropagation()}>
            <select
                aria-label="Локальный статус"
                title="Внутренняя отметка, не влияет на обработку платежа"
                className="w-full min-w-0 max-w-full rounded-md border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 p-2 text-sm text-slate-900 dark:text-slate-100 disabled:opacity-50"
                value={annotationDraft(appeal).status}
                disabled={!!localStatusSaving[appeal.id]}
                onChange={event => setAnnotationDrafts(previous => ({ ...previous, [appeal.id]: { ...annotationDraft(appeal), status: event.target.value as SupportLocalStatus | '' } }))}
            >
                <option value="">Не установлен</option>
                {localStatusOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
            </select>
            {compact && <p className="mt-1 text-xs break-words">{localStatusOptions.find(option => option.value === annotationDraft(appeal).status)?.label ?? 'Не установлен'}</p>}
            {compact ? (
                <button type="button" className="mt-2 block w-full text-left text-xs text-slate-500 dark:text-slate-400 hover:underline" onClick={() => handleViewClick(appeal)}>
                    <span className="line-clamp-2 break-words">{appeal.support_local_status_comment || 'Добавить комментарий'}</span>
                    <span className="sr-only"> — редактировать комментарий</span>
                </button>
            ) : <Textarea
                aria-label="Комментарий к локальному статусу"
                placeholder="Комментарий к статусу"
                className="mt-2 min-h-[60px]"
                rows={2}
                maxLength={2000}
                value={annotationDraft(appeal).comment}
                disabled={!!localStatusSaving[appeal.id]}
                onChange={event => setAnnotationDrafts(previous => ({ ...previous, [appeal.id]: { ...annotationDraft(appeal), comment: event.target.value } }))}
            />}
            <Button
                type="button"
                variant="outline"
                size="sm"
                className="mt-2"
                disabled={!!localStatusSaving[appeal.id] || (compact ? annotationDraft(appeal).status === (appeal.support_local_status ?? '') : !annotationDirty(appeal))}
                onClick={() => handleAnnotationSave(appeal, compact)}
            >
                {localStatusSaving[appeal.id] ? 'Сохранение…' : 'Сохранить'}
            </Button>
            {annotationDirty(appeal) && <p className="mt-1 text-xs text-amber-700 dark:text-amber-300">Есть несохранённые изменения</p>}
            {localStatusErrors[appeal.id] && <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">{localStatusErrors[appeal.id]}</p>}
        </div>
    );

    const fetchAppeals = async (page: number = currentPage) => {
        const requestId = ++appealsRequestId.current;
        setIsLoading(true);
        setAppealsError(null);
        try {
            const statusToFetch = activeTab === 'resolved' ? 'resolved' : 'active';
            const response = await supportAPI.getAppeals(statusToFetch, dateFrom, dateTo, page, pageSize, debouncedSearch);
            if (requestId !== appealsRequestId.current) return;
            setAppeals(response.items);
            setTotalPages(response.pages);
            setTotalItems(response.total);
            setCurrentPage(response.page);
        } catch (error) {
            if (requestId !== appealsRequestId.current) return;
            setAppealsError('Не удалось загрузить споры. Попробуйте обновить список.');
            setAppeals([]);
            setTotalItems(0);
            setTotalPages(1);
            console.error('Failed to fetch appeals:', error);
        } finally {
            if (requestId === appealsRequestId.current) setIsLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchAppeals(1);
        return () => { appealsRequestId.current++; };
    }, [activeTab, dateFrom, dateTo, debouncedSearch, pageSize]);

    useEffect(() => {
        const timeoutId = window.setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
        }, 350);

        return () => window.clearTimeout(timeoutId);
    }, [searchQuery]);

    const handlePageChange = (page: number) => {
        if (isLoading) return;
        fetchAppeals(page);
    };

    const handleResolveClick = async (appeal: SupportAppeal) => {
        setSelectedAppeal(appeal);
        setResolution(undefined);
        setReason('');

        // Сбрасываем предыдущие данные
        setSelectedTransaction(null);
        setReceiptUrl(null);

        setIsModalOpen(true);

        // Получаем данные транзакции
        if (appeal.transaction_id) {
            try {
                setReceiptUrl(paymentsAPI.getReceipt(appeal.transaction_id));

                const transactionData = await paymentsAPI.getPaymentStatus(appeal.transaction_id);
                setSelectedTransaction(transactionData);
            } catch (error) {
                console.error("Ошибка при получении данных транзакции:", error);
            }
        }

    };

    const handleAssignClick = async (appeal: SupportAppeal) => {
        if (!window.confirm('Взять апелляцию в работу?')) return;

        setIsLoading(true);
        try {
            await supportAPI.assignAppeal(appeal.id);
            fetchAppeals(currentPage);
        } catch (error) {
            console.error('Failed to assign appeal:', error);
            alert('Не удалось взять в работу');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewClick = async (appeal: SupportAppeal) => {
        // Сначала сбрасываем состояния
        setResolution(undefined);
        setReason('');
        setSelectedTransaction(null);
        setReceiptUrl(null);
        setIsReopenMode(false);
        setReopenFiles([]);
        setReopenDescription('');

        // View mode is similar to resolve mode but read-only
        setSelectedAppeal(appeal);
        // If resolved, set the resolution and reason from the appeal
        if (appeal.resolution_type) {
            setResolution(appeal.resolution_type as AppealResolution);
        }
        if (appeal.resolution_reason) {
            setReason(appeal.resolution_reason);
        }

        setIsModalOpen(true);

        if (appeal.transaction_id) {
            try {
                setReceiptUrl(paymentsAPI.getReceipt(appeal.transaction_id));
                const transactionData = await paymentsAPI.getPaymentStatus(appeal.transaction_id);
                setSelectedTransaction(transactionData);
            } catch (error) {
                console.error("Ошибка при получении данных транзакции:", error);
            }
        }
    };

    const handleSubmitResolution = async () => {
        if (!selectedAppeal || !reason.trim() || !resolution || !confirmResolution || resolutionInFlight.current) return;
        resolutionInFlight.current = true;
        setResolutionError('');

        setIsSubmitting(true);
        try {
            await supportAPI.resolveAppeal(selectedAppeal.id, {
                resolution,
                reason
            });
            setIsModalOpen(false);
            setConfirmResolution(false);
            fetchAppeals(); // Refresh list
        } catch (error: any) {
            console.error('Failed to resolve appeal:', error);
            setResolutionError(typeof error?.response?.data?.detail === 'string' ? error.response.data.detail : 'Ошибка при решении спора');
        } finally {
            resolutionInFlight.current = false;
            setIsSubmitting(false);
        }
    };

    const handleReopenClick = async (appeal: SupportAppeal) => {
        setSelectedAppeal(appeal);
        setIsReopenMode(true);
        setReopenFiles([]);
        setReopenDescription('');
        setResolution(undefined);
        setReason('');
        setSelectedTransaction(null);
        setReceiptUrl(null);
        setIsModalOpen(true);

        if (appeal.transaction_id) {
            try {
                setReceiptUrl(paymentsAPI.getReceipt(appeal.transaction_id));
                const transactionData = await paymentsAPI.getPaymentStatus(appeal.transaction_id);
                setSelectedTransaction(transactionData);
            } catch (error) {
                console.error("Ошибка при получении данных транзакции:", error);
            }
        }
    };

    const handleSubmitReopen = async () => {
        if (!selectedAppeal) return;
        if (reopenFiles.length === 0) {
            alert('Файл обязателен для переоткрытия апелляции');
            return;
        }

        setIsSubmitting(true);
        try {
            await supportAPI.reopenAppeal(
                selectedAppeal.id,
                reopenFiles,
                reopenDescription || undefined
            );
            setIsModalOpen(false);
            setIsReopenMode(false);
            fetchAppeals();
            alert('Апелляция успешно переоткрыта');
        } catch (error: any) {
            console.error('Failed to reopen appeal:', error);
            const errorMessage = error?.response?.data?.detail || 'Ошибка при переоткрытии апелляции';
            alert(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const next = [...reopenFiles, ...Array.from(e.target.files)];
            if (next.length > 10) {
                alert('Можно прикрепить не более 10 файлов');
            } else {
                const oversized = next.find((file) => file.size > 10 * 1024 * 1024);
                if (oversized) {
                    alert(`Файл «${oversized.name}» превышает лимит 10 МБ`);
                } else {
                    setReopenFiles(next);
                }
            }
            e.target.value = '';
        }
    };

    const handleCloseModal = () => {
        if (resolutionInFlight.current) return;
        setConfirmResolution(false);
        setAppealRecalculation(false);
        setResolutionError('');
        setIsModalOpen(false);
        setIsReopenMode(false);
        setReopenFiles([]);
        setReopenDescription('');
    };

    const handleTransactionLookup = async () => {
        const query = transactionLookupQuery.trim();
        if (!query) return;
        setIsTransactionLookupLoading(true);
        setTransactionLookupError(null);
        try {
            const response = await supportAPI.lookupTransactions(query);
            setTransactionLookupResults(response.items);
            if (response.items.length === 0) {
                setTransactionLookupError('Транзакции с таким номером не найдены');
            }
        } catch (error: any) {
            setTransactionLookupResults([]);
            setTransactionLookupError(error?.response?.data?.detail || 'Ошибка поиска транзакции');
        } finally {
            setIsTransactionLookupLoading(false);
        }
    };

    const canReopenAppeal = (appeal: SupportAppeal | null) => {
        if (!appeal || appeal.externally_managed) return false;
        return [AppealStatus.RESOLVED, AppealStatus.REJECTED, AppealStatus.CANCELLED].includes(appeal.status as AppealStatus);
    };

    const formatExternalTtl = (ttl?: string | number) => {
        if (ttl == null || ttl === '') return '—';
        if (typeof ttl === 'number' || /^\d+$/.test(ttl)) {
            const seconds = Number(ttl);
            const hours = Math.floor(seconds / 3600);
            const minutes = Math.floor((seconds % 3600) / 60);
            return hours > 0 ? `${hours} ч ${minutes} мин` : `${minutes} мин`;
        }
        return formatDate(String(ttl));
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case AppealStatus.OPEN:
                return <Badge className="bg-yellow-500">На этапе трейдера</Badge>;
            case AppealStatus.PENDING_SUPPORT:
                return <Badge className="bg-purple-500">Ожидает саппорта</Badge>;
            case AppealStatus.IN_PROGRESS:
                return <Badge className="bg-indigo-500">В работе</Badge>;
            case AppealStatus.RESOLVED:
                return <Badge className="bg-green-500">Решен</Badge>;
            case AppealStatus.REJECTED:
                return <Badge className="bg-red-500">Отклонен</Badge>;
            case AppealStatus.CANCELLED:
                return <Badge className="bg-gray-500">Отменен</Badge>;
            default:
                return <Badge>{status}</Badge>;
        }
    };

    const getTransactionStatusBadge = (status: string) => {
        switch (status) {
            case 'completed': return <Badge className="bg-green-500">Завершен</Badge>;
            case 'cancelled': return <Badge className="bg-red-500">Отменен</Badge>;
            case 'assigned': return <Badge className="bg-blue-500">Назначен</Badge>;
            case 'pending_requisites': return <Badge className="bg-yellow-400 text-yellow-900 dark:text-yellow-200">Ожидает реквизитов</Badge>;
            case 'receipt_uploaded': return <Badge className="bg-yellow-500">Чек загружен</Badge>;
            case 'confirmed': return <Badge className="bg-purple-500">Подтвержден</Badge>;
            case 'disputed': return <Badge className="bg-orange-500">Спор</Badge>;
            case 'no_traders': return <Badge variant="outline" className="text-gray-500 dark:text-slate-400">Нет трейдеров</Badge>;
            default: return <Badge variant="outline">{status}</Badge>;
        }
    };

    // Назначение фиксирует взявшего в работу; решение доступно любому активному саппорту.
    const isNorosAppeal = selectedAppeal?.external_provider === 'noros';
    const isReadOnly = Boolean(selectedAppeal?.externally_managed) || !(
        (isNorosAppeal && selectedAppeal?.status === AppealStatus.OPEN) ||
        selectedAppeal?.status === AppealStatus.PENDING_SUPPORT ||
        selectedAppeal?.status === AppealStatus.IN_PROGRESS
    );

    useEffect(() => {
        setConfirmResolution(false);
        setResolutionError('');
    }, [resolution, reason, selectedAppeal?.id, isReopenMode, isModalOpen]);
    useEffect(() => { setAppealRecalculation(false); }, [selectedAppeal?.id, isReopenMode]);
    const supportIdentity = user?.id || user?.username || '';
    const canRecalculateAppeal = !isReadOnly && !isReopenMode && !selectedAppeal?.external_provider && selectedAppeal?.locally_recalculable === true;
    const refreshAfterRecalculation = () => {
        void fetchAppeals();
        if (transactionLookupQuery.trim()) void handleTransactionLookup();
        setIsModalOpen(false);
        setAppealRecalculation(false);
    };

    const isWithdrawAppeal =
        selectedAppeal?.transaction_type === 'withdraw' ||
        selectedTransaction?.order_info?.order_type === 'withdraw';

    const appealAttachments = useMemo(() => {
        return [...(selectedAppeal?.attachments || [])].sort((a, b) => {
            return parseSupportDate(a.created_at).getTime() - parseSupportDate(b.created_at).getTime();
        });
    }, [selectedAppeal?.attachments]);

    return (
        <div className="support-dashboard min-h-screen bg-slate-50 dark:bg-slate-900 p-3 sm:p-6">
            <header className="flex flex-wrap gap-3 justify-between items-center mb-8 bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">Support Dashboard</h1>
                    <p className="text-slate-500 dark:text-slate-400">Добро пожаловать, {user?.username}</p>
                </div>
                <div className="flex flex-wrap gap-2 sm:gap-4">
                    <Button variant="outline" onClick={() => fetchAppeals(currentPage)} disabled={isLoading}>
                        <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
                        Обновить
                    </Button>
                    <Button variant="destructive" onClick={logout}>
                        <LogOut className="mr-2 h-4 w-4" />
                        Выйти
                    </Button>
                    <ThemeToggle />
                </div>
            </header>

            <div className="max-w-7xl mx-auto">
                <Card className="mb-6 border-blue-200 dark:border-blue-800">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Search className="h-5 w-5 text-blue-600 dark:text-blue-300" />
                            Проверка транзакции
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="flex flex-col gap-2 sm:flex-row">
                            <Input
                                value={transactionLookupQuery}
                                onChange={(e) => setTransactionLookupQuery(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') handleTransactionLookup();
                                }}
                                placeholder="Transaction ID, payment_id или Merchant Payment/Withdrawal ID"
                                className="font-mono"
                            />
                            <Button onClick={handleTransactionLookup} disabled={isTransactionLookupLoading || !transactionLookupQuery.trim()}>
                                {isTransactionLookupLoading ? <RefreshCw className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                                Проверить
                            </Button>
                        </div>

                        {transactionLookupError && (
                            <div className="rounded border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950 p-3 text-sm text-red-700 dark:text-red-300">
                                {transactionLookupError}
                            </div>
                        )}

                        {transactionLookupResults.map((transaction) => {
                            const requisites = transaction.order_type === 'withdraw'
                                ? transaction.user_requisites
                                : transaction.trader_requisites;
                            return (
                                <div key={transaction.transaction_id} className="rounded-lg border bg-slate-50 dark:bg-slate-900 p-4">
                                    <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
                                        <div>
                                            <div className="font-mono text-sm font-semibold">{transaction.transaction_id}</div>
                                            <div className="mt-1 flex flex-wrap gap-2 text-xs text-slate-500 dark:text-slate-400">
                                                {transaction.payment_id && <span>payment_id: {transaction.payment_id}</span>}
                                                {transaction.merchant_payment_id && <span>merchantPaymentId: {transaction.merchant_payment_id}</span>}
                                                {transaction.merchant_withdrawal_id && <span>merchantWithdrawalId: {transaction.merchant_withdrawal_id}</span>}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Badge variant="outline">{transaction.order_type === 'withdraw' ? 'Вывод' : 'Пополнение'}</Badge>
                                            {getTransactionStatusBadge(transaction.status)}
                                            <Button variant="outline" onClick={() => {
                                                setRecalculationTransactionId(transaction.transaction_id); setActiveTab('recalculation');
                                            }}>Перерасчёт</Button>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 text-sm md:grid-cols-3">
                                        <div className="space-y-2">
                                            <div><span className="text-slate-500 dark:text-slate-400">Отправлено:</span> <strong>{transaction.payer_amount} {transaction.payer_currency}</strong></div>
                                            <div><span className="text-slate-500 dark:text-slate-400">Получение:</span> <strong>{transaction.receiver_amount} {transaction.receiver_currency}</strong></div>
                                            <div><span className="text-slate-500 dark:text-slate-400">Создано:</span> {formatDate(transaction.assigned_at)}</div>
                                            {transaction.cancellation_reason && <div className="text-red-700 dark:text-red-300">Причина: {transaction.cancellation_reason}</div>}
                                        </div>
                                        <div className="space-y-2">
                                            <div><span className="text-slate-500 dark:text-slate-400">Мерчант:</span> {transaction.merchant?.name || '-'}</div>
                                            <div><span className="text-slate-500 dark:text-slate-400">Трейдер:</span> {transaction.trader?.username || '-'}</div>
                                            {transaction.appeal ? (
                                                <div className="rounded border border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-950 p-2">
                                                    <div>Апелляция: <span className="font-mono">{transaction.appeal.id}</span></div>
                                                    <div>Статус: {transaction.appeal.status}</div>
                                                    <Button
                                                        type="button"
                                                        variant="link"
                                                        size="sm"
                                                        className="h-auto p-0"
                                                        onClick={() => {
                                                            setSearchQuery(transaction.appeal!.id);
                                                            setActiveTab(transaction.appeal!.status === 'resolved' ? 'resolved' : 'active');
                                                        }}
                                                    >
                                                        Найти в списке апелляций
                                                    </Button>
                                                </div>
                                            ) : (
                                                <div className="text-slate-500 dark:text-slate-400">Апелляция ещё не создана</div>
                                            )}
                                        </div>
                                        <div className="space-y-2">
                                            <div className="font-medium">Реквизиты</div>
                                            {requisites && Object.keys(requisites).length > 0 ? (
                                                Object.entries(requisites).map(([key, value]) => value != null && value !== '' && (
                                                    <div key={key} className="flex justify-between gap-3">
                                                        <span className="text-slate-500 dark:text-slate-400">{key}:</span>
                                                        <span className="break-all text-right font-mono">{String(value)}</span>
                                                    </div>
                                                ))
                                            ) : (
                                                <div className="text-slate-500 dark:text-slate-400">Нет реквизитов</div>
                                            )}
                                        </div>
                                    </div>

                                    {transaction.receipt_filename && (
                                        <Button variant="outline" size="sm" className="mt-4" asChild>
                                            <a href={paymentsAPI.getReceipt(transaction.transaction_id)} target="_blank" rel="noopener noreferrer">
                                                <FileText className="mr-2 h-4 w-4" />
                                                Открыть чек
                                            </a>
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </CardContent>
                </Card>

                <Card className="mb-6">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2">
                            <Calendar className="h-5 w-5" />
                            Фильтры
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex flex-wrap gap-4 items-end">
                            <div className="space-y-2">
                                <Label htmlFor="appealSearch">Поиск</Label>
                                <div className="relative">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <Input
                                        id="appealSearch"
                                        type="search"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        placeholder="Глобальный поиск: ID, сумма, мерчант, трейдер, реквизиты"
                                        className="w-80 pl-9"
                                    />
                                </div>
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="dateFrom">Дата от</Label>
                                <Input
                                    id="dateFrom"
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className="w-40"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="dateTo">Дата до</Label>
                                <Input
                                    id="dateTo"
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className="w-40"
                                />
                            </div>
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setSearchQuery('');
                                    setDebouncedSearch('');
                                    setDateFrom('');
                                    setDateTo('');
                                }}
                            >
                                Сбросить
                            </Button>
                        </div>
                    </CardContent>
                </Card>

                <SupportPendingRecalculations user={supportIdentity} onCompleted={refreshAfterRecalculation}
                    excludedKeys={[
                        ...(activeTab === 'recalculation' ? [supportRecalculationKey(supportIdentity)] : []),
                        ...(isModalOpen && appealRecalculation && selectedAppeal ? [supportRecalculationKey(supportIdentity, selectedAppeal.id)] : []),
                    ]} />
                <Tabs defaultValue="active" value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <TabsList className="mb-4">
                        <TabsTrigger value="active">Активные споры</TabsTrigger>
                        <TabsTrigger value="resolved">Решенные</TabsTrigger>
                        <TabsTrigger value="recalculation">Перерасчёт</TabsTrigger>
                    </TabsList>

                    <TabsContent value="recalculation">
                        <SupportRecalculationPanel key={`${supportIdentity}:${recalculationTransactionId}`} user={supportIdentity}
                            transactionId={recalculationTransactionId} onCompleted={refreshAfterRecalculation} />
                    </TabsContent>
                    <TabsContent value={activeTab === 'recalculation' ? 'active' : activeTab}>
                        <Card>
                            <CardHeader>
                                <CardTitle>Список апелляций ({totalItems})</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {appealsError ? (
                                    <p role="alert" className="py-4 text-red-600 dark:text-red-400">{appealsError}</p>
                                ) : isLoading ? (
                                    <p className="py-4 text-slate-500 dark:text-slate-400">Загрузка…</p>
                                ) : appeals.length === 0 ? (
                                    <div className="text-center py-10 text-slate-500 dark:text-slate-400">
                                        Нет апелляций в этом статусе
                                    </div>
                                ) : (
                                    <>
                                        <div className="min-w-0">
                                            <table className="support-appeals-table w-full table-fixed text-sm text-left">
                                                <thead className="text-xs text-slate-500 dark:text-slate-400 uppercase bg-slate-50 dark:bg-slate-900">
                                                    <tr>
                                                        <th scope="col">ID / Дата</th>
                                                        <th scope="col">Статус / Таймер</th>
                                                        <th scope="col">Мерчант / Трейдер</th>
                                                        <th scope="col">Сумма / Тип / Провайдер</th>
                                                        <th scope="col">Локальный статус</th>
                                                        <th scope="col">Действия</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {appeals.map((appeal) => (
                                                        <tr key={appeal.id} className="border-b hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer transition-colors" onClick={() => handleViewClick(appeal)}>
                                                            <td data-label="ID / Дата">
                                                                <div className="font-medium text-slate-900 dark:text-slate-100 text-xs font-mono break-all">{appeal.id}</div>
                                                                <div className="text-slate-500 dark:text-slate-400 text-xs">{formatDate(appeal.created_at)}</div>
                                                            </td>
                                                            <td data-label="Статус / Таймер">
                                                                {getStatusBadge(appeal.status)}
                                                                {appeal.resolution_type && (
                                                                    <div className="text-xs mt-1 text-slate-500 dark:text-slate-400">
                                                                        {getResolutionLabel(appeal)}
                                                                    </div>
                                                                )}
                                                                {appeal.resolution_reason && (
                                                                    <div className="text-xs mt-1 text-slate-600 dark:text-slate-300 italic line-clamp-2">
                                                                        {appeal.resolution_reason}
                                                                    </div>
                                                                )}
                                                                {(appeal.assigned_to || appeal.resolved_by) && <div className="mt-1 space-y-1 text-xs [overflow-wrap:anywhere]">
                                                                    <div className="flex items-start gap-1 text-indigo-600 dark:text-indigo-300">
                                                                        <User className="mt-0.5 h-3 w-3 shrink-0" />
                                                                        <span>Взял в работу: {appeal.assigned_to || '—'}</span>
                                                                    </div>
                                                                    <div className="flex items-start gap-1 text-green-600 dark:text-green-300">
                                                                        <User className="mt-0.5 h-3 w-3 shrink-0" />
                                                                        <span>Вынес решение: {appeal.resolved_by ? formatResolvedBy(appeal.resolved_by) : '—'}</span>
                                                                    </div>
                                                                </div>}
                                                                <div className="mt-2">
                                                                    {appeal.externally_managed ? (
                                                                        <div className="text-xs text-blue-700 dark:text-blue-300">
                                                                            <div className="font-medium">Статус провайдера: {externalStatusLabel(appeal.external_appeal_status)}</div>
                                                                            {appeal.external_ttl && <div className="text-slate-500 dark:text-slate-400">TTL: {formatExternalTtl(appeal.external_ttl)}</div>}
                                                                        </div>
                                                                    ) : (
                                                                        <AppealTimer
                                                                            createdAt={appeal.created_at}
                                                                            lifetimeMinutes={appeal.appeal_lifetime_minutes || 180}
                                                                        />
                                                                    )}
                                                                </div>
                                                            </td>
                                                            <td data-label="Мерчант / Трейдер">
                                                                <div className="space-y-2">
                                                                    <div>
                                                                        <div className="text-xs text-slate-500 dark:text-slate-400">Мерчант</div>
                                                                        <div className="flex items-center gap-1">
                                                                            <Store className="h-3 w-3 text-slate-400" />
                                                                            <span className="text-sm">{appeal.merchant_name || '-'}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div>
                                                                        <div className="text-xs text-slate-500 dark:text-slate-400">Трейдер</div>
                                                                        <div className="flex items-center gap-1">
                                                                            <Users className="h-3 w-3 text-slate-400" />
                                                                            <span className="text-sm">{appeal.trader_name || '-'}</span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td data-label="Сумма / Тип / Провайдер">
                                                                <span className="font-bold">{appeal.amount} {appeal.currency}</span>
                                                                <div className="mt-2">
                                                                    <div className="flex flex-col items-start gap-1">
                                                                        {appeal.transaction_type === 'withdraw' ? (
                                                                            <Badge variant="outline" className="border-red-500 text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-950">
                                                                                Вывод
                                                                            </Badge>
                                                                        ) : (
                                                                            <Badge variant="outline" className="border-green-500 text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-950">
                                                                                Пополнение
                                                                            </Badge>
                                                                        )}
                                                                        {isExternalAppeal(appeal) && (
                                                                            <Badge variant="outline" className="border-blue-500 bg-blue-50 dark:bg-blue-950 text-blue-700 dark:text-blue-300 whitespace-normal">
                                                                                Внешняя апелляция · {getExternalProviderName(appeal)}
                                                                            </Badge>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </td>
                                                            <td data-label="Локальный статус">
                                                                {renderLocalStatus(appeal, true)}
                                                            </td>
                                                            <td data-label="Действия" onClick={event => event.stopPropagation()}>
                                                                {appeal.externally_managed ? (
                                                                    <Button variant="ghost" size="sm" onClick={() => handleViewClick(appeal)}>
                                                                        <Eye className="h-4 w-4 mr-2" />
                                                                        Просмотр
                                                                    </Button>
                                                                ) : (appeal.status === AppealStatus.PENDING_SUPPORT || (appeal.external_provider === 'noros' && appeal.status === AppealStatus.OPEN)) ? (
                                                                    <Button size="sm" onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleAssignClick(appeal);
                                                                    }}>
                                                                        Взять в работу
                                                                    </Button>
                                                                ) : appeal.status === AppealStatus.IN_PROGRESS ? (
                                                                    <Button size="sm" onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        handleResolveClick(appeal);
                                                                    }}>
                                                                        Решить спор
                                                                    </Button>
                                                                ) : (
                                                                    <Button variant="ghost" size="sm" onClick={() => handleViewClick(appeal)}>
                                                                        <Eye className="h-4 w-4 mr-2" />
                                                                        Просмотр
                                                                    </Button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>

                                    </>
                                )}
                                <Pagination
                                    currentPage={currentPage}
                                    totalPages={totalPages}
                                    onPageChange={handlePageChange}
                                    totalItems={totalItems}
                                    pageSize={pageSize}
                                    disabled={isLoading}
                                    onPageSizeChange={setPageSize}
                                />
                            </CardContent>
                        </Card>
                    </TabsContent>
                </Tabs>
            </div>

            {/* Модальное окно решения/просмотра спора */}
            <Dialog open={isModalOpen} onOpenChange={handleCloseModal}>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="break-all pr-6">
                            {isReopenMode ? 'Переоткрытие спора' : (isReadOnly ? 'Просмотр спора' : 'Решение спора')} #{selectedAppeal?.id}
                        </DialogTitle>
                        <DialogDescription>
                            Транзакция: {selectedAppeal?.transaction_id}
                            {selectedAppeal?.merchant_name && (
                                <span className="ml-2">| Мерчант: <strong>{selectedAppeal.merchant_name}</strong></span>
                            )}
                            {selectedAppeal?.trader_name && (
                                <span className="ml-2">| Трейдер: <strong>{selectedAppeal.trader_name}</strong></span>
                            )}
                        </DialogDescription>
                    </DialogHeader>

                    {selectedAppeal && <div className="my-4 grid grid-cols-1 gap-3 rounded-lg border bg-slate-50 p-3 text-sm dark:bg-slate-900 sm:grid-cols-2">
                        <div className="min-w-0 [overflow-wrap:anywhere]">
                            <p><span className="text-slate-500 dark:text-slate-400">Взял в работу:</span> <span className="font-medium">{selectedAppeal.assigned_to || '—'}</span></p>
                            {selectedAppeal.assigned_at && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formatDate(selectedAppeal.assigned_at)}</p>}
                        </div>
                        <div className="min-w-0 [overflow-wrap:anywhere]">
                            <p><span className="text-slate-500 dark:text-slate-400">Вынес решение:</span> <span className="font-medium">{selectedAppeal.resolved_by ? formatResolvedBy(selectedAppeal.resolved_by) : '—'}</span></p>
                            {selectedAppeal.resolved_at && <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{formatDate(selectedAppeal.resolved_at)}</p>}
                        </div>
                    </div>}

                    {canRecalculateAppeal && <fieldset className="my-4 min-w-0" disabled={isSubmitting}>
                        <legend className="mb-2 text-sm font-medium">Способ решения</legend>
                        <div className="grid grid-cols-1 gap-2 rounded-lg bg-slate-100 p-1 dark:bg-slate-900 sm:grid-cols-2">
                            {[{ value: false, label: 'Без изменения суммы' }, { value: true, label: 'С новой суммой' }].map(mode =>
                                <label key={String(mode.value)} className="min-w-0 cursor-pointer">
                                    <input type="radio" name="appeal-resolution-mode" className="peer sr-only"
                                        checked={appealRecalculation === mode.value} onChange={() => {
                                            setAppealRecalculation(mode.value); setConfirmResolution(false);
                                        }} />
                                    <span className="block rounded-md px-3 py-2 text-center text-sm text-slate-600 peer-checked:bg-white peer-checked:font-semibold peer-checked:text-slate-900 peer-checked:shadow-sm peer-focus-visible:ring-2 peer-focus-visible:ring-blue-500 dark:text-slate-400 dark:peer-checked:bg-slate-700 dark:peer-checked:text-white">{mode.label}</span>
                                </label>)}
                        </div>
                        <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">{appealRecalculation
                            ? 'Укажите фактическую сумму. После проверки расчёта сделка будет завершена, а спор закрыт.'
                            : 'Выберите сторону и укажите причину. Перед отправкой вы проверите итог решения.'}</p>
                    </fieldset>}

                    {selectedAppeal && (
                        <div className="space-y-2">
                            <p className="text-sm font-medium">Локальный статус</p>
                            {renderLocalStatus(selectedAppeal)}
                            <details className="text-sm">
                                <summary className="cursor-pointer">История статусов и комментариев ({selectedAppeal.support_local_status_history?.length ?? 0})</summary>
                                <div className="mt-2 max-h-64 space-y-3 overflow-y-auto">
                                    {!(selectedAppeal.support_local_status_history?.length) && <p className="text-slate-500">Изменений пока нет</p>}
                                    {[...(selectedAppeal.support_local_status_history ?? [])].reverse().map((entry, index) => (
                                        <div key={index} className="rounded border border-slate-200 dark:border-slate-700 p-2">
                                            <p className="text-xs text-slate-500">{formatDate(entry.changed_at)} · {entry.changed_by}</p>
                                            <p>{localStatusOptions.find(option => option.value === entry.previous_status)?.label ?? 'Не установлен'} → {localStatusOptions.find(option => option.value === entry.status)?.label ?? 'Не установлен'}</p>
                                            <p className="whitespace-pre-wrap break-words">Комментарий: {entry.comment || '—'}</p>
                                            {entry.previous_comment !== entry.comment && entry.previous_comment && <p className="whitespace-pre-wrap break-words text-slate-500">Ранее: {entry.previous_comment}</p>}
                                        </div>
                                    ))}
                                </div>
                            </details>
                            <p className="text-xs text-slate-500 dark:text-slate-400">Внутренняя отметка, не влияет на обработку платежа</p>
                        </div>
                    )}

                    {selectedAppeal && isExternalAppeal(selectedAppeal) && (
                        <div className="rounded-lg border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-950 p-4 [overflow-wrap:anywhere]">
                            <div className="mb-3 flex flex-wrap items-center gap-2">
                                <Badge className="bg-blue-600">
                                    Внешняя апелляция · {getExternalProviderName(selectedAppeal)}
                                </Badge>
                                {selectedAppeal.externally_managed && <Badge variant="outline">Только просмотр</Badge>}
                            </div>
                            <div className="grid grid-cols-1 gap-2 text-sm md:grid-cols-2">
                                <div><span className="text-slate-500 dark:text-slate-400">Внешний ID:</span> <span className="font-mono">{selectedAppeal.external_appeal_id || '—'}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">Статус провайдера:</span> <span className="font-medium">{externalStatusLabel(selectedAppeal.external_appeal_status)}</span>{selectedAppeal.external_appeal_status && <span className="ml-2 font-mono text-xs">({selectedAppeal.external_appeal_status})</span>}</div>
                                <div><span className="text-slate-500 dark:text-slate-400">Reason code:</span> <span className="font-mono">{selectedAppeal.external_reason_code || '—'}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">TTL:</span> <span>{formatExternalTtl(selectedAppeal.external_ttl)}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">Средства заблокированы:</span> <span>{selectedAppeal.external_blocked == null ? '—' : selectedAppeal.external_blocked ? 'Да' : 'Нет'}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">Последняя синхронизация:</span> <span>{formatDate(selectedAppeal.external_synced_at)}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">Нужен чек клиента:</span> <span>{selectedAppeal.external_client_invoice_required == null ? '—' : selectedAppeal.external_client_invoice_required ? 'Да' : 'Нет'}</span></div>
                                <div><span className="text-slate-500 dark:text-slate-400">Нужен чек трейдера:</span> <span>{selectedAppeal.external_trader_invoice_required == null ? '—' : selectedAppeal.external_trader_invoice_required ? 'Да' : 'Нет'}</span></div>
                            </div>
                            {selectedAppeal.external_comment && (
                                <div className="mt-3 rounded border border-blue-100 dark:border-blue-800 bg-white dark:bg-slate-800 p-3 text-sm">
                                    {selectedAppeal.external_comment}
                                </div>
                            )}
                            {selectedAppeal.external_sync_error && (
                                <div className="mt-3 text-sm text-red-700 dark:text-red-300">
                                    Ошибка синхронизации: {selectedAppeal.external_sync_error}
                                </div>
                            )}
                        </div>
                    )}

                    <div className={`grid grid-cols-1 gap-6 py-4 ${appealRecalculation ? '' : 'md:grid-cols-2'}`}>
                        {/* Левая колонка - Информация о транзакции */}
                        <div className="space-y-6">
                            <div className="bg-slate-50 dark:bg-slate-900 p-4 rounded-lg border">
                                <h3 className="font-semibold mb-4 flex items-center gap-2">
                                    <AlertCircle className="h-4 w-4" />
                                    Детали транзакции
                                </h3>

                                {selectedTransaction ? (
                                    <div className="space-y-3 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-slate-500 dark:text-slate-400">Статус:</span>
                                            {getTransactionStatusBadge(selectedTransaction.status)}
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-500 dark:text-slate-400">Сумма:</span>
                                            {selectedTransaction.order_info?.order_type === 'withdraw' ? (
                                                <span className="font-mono font-bold">{selectedTransaction.receiver_amount} {selectedTransaction.receiver_currency}</span>
                                            ) : (
                                                <span className="font-mono font-bold">{selectedTransaction.payer_amount} {selectedTransaction.payer_currency}</span>
                                            )}
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-slate-500 dark:text-slate-400">Тип ордера:</span>
                                            <span className="capitalize">{selectedTransaction.order_info?.order_type || 'N/A'}</span>
                                        </div>

                                        <div className="border-t pt-3 mt-3 space-y-2">
                                            <h4 className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                                                <Clock className="h-3 w-3" /> История событий
                                            </h4>
                                            <div className="grid grid-cols-[1fr_auto] gap-2 text-xs">
                                                <span>Создан:</span>
                                                <span className="font-mono">{formatDate(selectedTransaction.assigned_at)}</span>

                                                {selectedTransaction.receipt_uploaded_at && (
                                                    <>
                                                        <span>Чек загружен:</span>
                                                        <span className="font-mono">{formatDate(selectedTransaction.receipt_uploaded_at)}</span>
                                                    </>
                                                )}

                                                {selectedTransaction.confirmed_at && (
                                                    <>
                                                        <span>Подтвержден:</span>
                                                        <span className="font-mono">{formatDate(selectedTransaction.confirmed_at)}</span>
                                                    </>
                                                )}

                                                {selectedTransaction.completed_at && (
                                                    <>
                                                        <span>Завершен:</span>
                                                        <span className="font-mono">{formatDate(selectedTransaction.completed_at)}</span>
                                                    </>
                                                )}

                                                {selectedTransaction.cancelled_at && (
                                                    <>
                                                        <span>Отменен:</span>
                                                        <span className="font-mono">{formatDate(selectedTransaction.cancelled_at)}</span>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {(selectedTransaction.trader_commission_rate != null ||
                                            selectedTransaction.trader_commission_amount != null ||
                                            selectedTransaction.merchant_commission_rate != null ||
                                            selectedTransaction.merchant_commission_amount != null) && (
                                                <div className="border-t pt-3 mt-3 space-y-2">
                                                    <h4 className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                                                        Комиссии транзакции
                                                    </h4>
                                                    <div className="grid grid-cols-[1fr_auto] gap-2 text-xs">
                                                        {selectedTransaction.trader_commission_rate != null && (
                                                            <>
                                                                <span className="text-slate-500 dark:text-slate-400">Ставка трейдера:</span>
                                                                <span className="font-mono font-medium">{selectedTransaction.trader_commission_rate}%</span>
                                                            </>
                                                        )}
                                                        {selectedTransaction.trader_commission_amount != null && (
                                                            <>
                                                                <span className="text-slate-500 dark:text-slate-400">Комиссия трейдера:</span>
                                                                <span className="font-mono font-medium text-blue-700 dark:text-blue-300">{selectedTransaction.trader_commission_amount.toFixed(2)} USDT</span>
                                                            </>
                                                        )}
                                                        {selectedTransaction.merchant_commission_rate != null && (
                                                            <>
                                                                <span className="text-slate-500 dark:text-slate-400">Ставка мерчанта:</span>
                                                                <span className="font-mono font-medium">{selectedTransaction.merchant_commission_rate}%</span>
                                                            </>
                                                        )}
                                                        {(selectedTransaction.merchant_commission_amount != null ||
                                                            (selectedTransaction.merchant_commission_rate != null && selectedTransaction.payer_amount != null)) && (
                                                                <>
                                                                    <span className="text-slate-500 dark:text-slate-400">Комиссия мерчанта:</span>
                                                                    <span className="font-mono font-medium text-orange-700 dark:text-orange-300">
                                                                        {(
                                                                            selectedTransaction.merchant_commission_amount ??
                                                                            (selectedTransaction.payer_amount * selectedTransaction.merchant_commission_rate! / 100)
                                                                        ).toFixed(2)}
                                                                    </span>
                                                                </>
                                                            )}
                                                    </div>
                                                </div>
                                            )}

                                        {selectedTransaction.notes && (
                                            <div className="border-t pt-3 mt-3">
                                                <h4 className="text-xs font-semibold uppercase text-slate-500 dark:text-slate-400 mb-2 flex items-center gap-1">
                                                    <MessageSquare className="h-3 w-3" /> Комментарий транзакции
                                                </h4>
                                                <div className="bg-white dark:bg-slate-800 p-2 rounded border text-xs text-slate-700 dark:text-slate-200 italic">
                                                    "{selectedTransaction.notes}"
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center justify-center h-32 text-slate-400">
                                        <RefreshCw className="h-6 w-6 animate-spin" />
                                    </div>
                                )}
                            </div>

                            {/* Реквизиты платежа */}
                            {(selectedAppeal?.trader_requisites || selectedAppeal?.user_requisites) && (
                                <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
                                    <h3 className="font-semibold mb-4 flex items-center gap-2 text-blue-800 dark:text-blue-200">
                                        <CreditCard className="h-4 w-4" />
                                        Реквизиты платежа
                                    </h3>

                                    {/* Реквизиты трейдера (для deposit) */}
                                    {selectedAppeal?.trader_requisites && (
                                        <div className="space-y-2 text-sm mb-4">
                                            <h4 className="text-xs font-semibold uppercase text-blue-600 dark:text-blue-300 mb-2">
                                                Реквизиты трейдера
                                            </h4>
                                            {selectedAppeal.trader_requisites.recipient_bank && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <Building className="h-3 w-3" /> Банк:
                                                    </span>
                                                    <span className="font-medium">{bankDisplayName(selectedAppeal.trader_requisites.recipient_bank)}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.trader_requisites.recipient_name && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <User className="h-3 w-3" /> Получатель:
                                                    </span>
                                                    <span className="font-medium">{selectedAppeal.trader_requisites.recipient_name}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.trader_requisites.phone_number && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <Phone className="h-3 w-3" /> Телефон:
                                                    </span>
                                                    <span className="font-medium font-mono">{selectedAppeal.trader_requisites.phone_number}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.trader_requisites.transfer_amount && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300">Сумма перевода:</span>
                                                    <span className="font-bold text-blue-700 dark:text-blue-300">{selectedAppeal.trader_requisites.transfer_amount} RUB</span>
                                                </div>
                                            )}
                                        </div>
                                    )}

                                    {/* Реквизиты клиента (для withdraw) */}
                                    {selectedAppeal?.user_requisites && (
                                        <div className="space-y-2 text-sm">
                                            <h4 className="text-xs font-semibold uppercase text-green-600 dark:text-green-300 mb-2">
                                                Реквизиты клиента
                                            </h4>
                                            {selectedAppeal.user_requisites.recipient_bank && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <Building className="h-3 w-3" /> Банк:
                                                    </span>
                                                    <span className="font-medium">{bankDisplayName(selectedAppeal.user_requisites.recipient_bank)}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.user_requisites.recipient_name && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <User className="h-3 w-3" /> Получатель:
                                                    </span>
                                                    <span className="font-medium">{selectedAppeal.user_requisites.recipient_name}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.user_requisites.phone_number && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <Phone className="h-3 w-3" /> Телефон:
                                                    </span>
                                                    <span className="font-medium font-mono">{selectedAppeal.user_requisites.phone_number}</span>
                                                </div>
                                            )}
                                            {selectedAppeal.user_requisites.recipient_card && (
                                                <div className="flex justify-between items-center">
                                                    <span className="text-slate-600 dark:text-slate-300 flex items-center gap-1">
                                                        <CreditCard className="h-3 w-3" /> Карта:
                                                    </span>
                                                    <span className="font-medium font-mono">{selectedAppeal.user_requisites.recipient_card}</span>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}

                            {selectedAppeal && (selectedAppeal.trader_response_description || selectedAppeal.trader_requested_amount != null) && (
                                <div className="border rounded-lg p-4 space-y-2">
                                    <h3 className="font-semibold text-sm">Ответ трейдера</h3>
                                    <TraderAppealResponse response={selectedAppeal} currency={selectedAppeal.currency} />
                                </div>
                            )}

                            {/* Доказательства */}
                            <div className="space-y-4">
                                <h3 className="font-semibold text-sm">Доказательства</h3>

                                {receiptUrl && selectedTransaction?.receipt_filename && (
                                    <div className="border rounded-lg p-3 flex items-center justify-between bg-white dark:bg-slate-800">
                                        <div className="flex items-center gap-3">
                                            <div className="h-10 w-10 bg-blue-100 dark:bg-blue-950 rounded-full flex items-center justify-center text-blue-600 dark:text-blue-300">
                                                <FileText className="h-5 w-5" />
                                            </div>
                                            <div>
                                                <div className="font-medium text-sm">Чек транзакции</div>
                                                <div className="text-xs text-slate-500 dark:text-slate-400">От плательщика</div>
                                            </div>
                                        </div>
                                        <Button variant="outline" size="sm" asChild>
                                            <a href={receiptUrl} target="_blank" rel="noopener noreferrer">
                                                Открыть <ExternalLink className="ml-2 h-3 w-3" />
                                            </a>
                                        </Button>
                                    </div>
                                )}

                                {appealAttachments.length > 0 && (
                                    <div className="border rounded-lg p-4 bg-white dark:bg-slate-800">
                                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                                            <Upload className="h-4 w-4" />
                                            Материалы апелляции
                                        </h4>
                                        <div className="space-y-3">
                                            {appealAttachments.map((attachment) => (
                                                <div key={attachment.id} className="border rounded-lg p-3 bg-slate-50 dark:bg-slate-900">
                                                    <div className="flex items-start justify-between gap-3">
                                                        <div className="flex items-start gap-3 min-w-0">
                                                            <div className="h-9 w-9 bg-orange-100 dark:bg-orange-950 rounded-full flex items-center justify-center text-orange-600 dark:text-orange-300 shrink-0">
                                                                <FileText className="h-4 w-4" />
                                                            </div>
                                                            <div className="min-w-0">
                                                                <div className="font-medium text-sm text-slate-900 dark:text-slate-100">
                                                                    {getAttachmentTypeLabel(attachment.type)}
                                                                </div>
                                                                <div className="text-xs text-slate-500 dark:text-slate-400 truncate">
                                                                    {getAttachmentFilename(attachment)}
                                                                </div>
                                                                <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                                                                    {getAttachmentRoleLabel(attachment.uploaded_by_role)}
                                                                    {attachment.uploaded_by ? `: ${attachment.uploaded_by}` : ''}
                                                                    {' | '}
                                                                    {formatDate(attachment.created_at)}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        <Button variant="outline" size="sm" asChild>
                                                            <a href={supportAPI.getAppealFileUrl(attachment.file_path)} target="_blank" rel="noopener noreferrer">
                                                                Открыть <ExternalLink className="ml-2 h-3 w-3" />
                                                            </a>
                                                        </Button>
                                                    </div>
                                                    {attachment.description && (
                                                        <div className="mt-3 text-sm bg-white dark:bg-slate-800 p-2 rounded border text-slate-700 dark:text-slate-200">
                                                            {attachment.description}
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {selectedAppeal?.external_materials && selectedAppeal.external_materials.length > 0 && (
                                    <div className="border rounded-lg p-4 bg-blue-50 dark:bg-blue-950 border-blue-200 dark:border-blue-800">
                                        <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-3 flex items-center gap-2">
                                            <ExternalLink className="h-4 w-4" />
                                            Материалы callback провайдера
                                        </h4>
                                        <div className="space-y-2">
                                            {selectedAppeal.external_materials.map((material, index) => (
                                                <div key={`${material.path || 'material'}-${index}`} className="flex items-center justify-between gap-3 rounded border bg-white dark:bg-slate-800 p-3">
                                                    <div className="min-w-0">
                                                        <div className="text-sm font-medium">{material.type || 'Файл провайдера'}</div>
                                                        <div className="truncate text-xs text-slate-500 dark:text-slate-400">{material.mime_type || material.path}</div>
                                                    </div>
                                                    {material.path && (
                                                        <Button variant="outline" size="sm" asChild>
                                                            <a href={material.path} target="_blank" rel="noopener noreferrer">
                                                                Открыть <ExternalLink className="ml-2 h-3 w-3" />
                                                            </a>
                                                        </Button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {!receiptUrl && appealAttachments.length === 0 && (!selectedAppeal?.external_materials || selectedAppeal.external_materials.length === 0) && (
                                    <div className="text-center py-4 text-slate-500 dark:text-slate-400 text-sm border border-dashed rounded-lg">
                                        Нет прикрепленных файлов или описания
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Правая колонка - Форма решения или переоткрытия */}
                        <div className="space-y-6">
                            {/* История решений (если есть) */}
                            {selectedAppeal?.resolution_history && selectedAppeal.resolution_history.length > 0 && (
                                <div className="bg-amber-50 dark:bg-amber-950 p-4 rounded-lg border border-amber-200 dark:border-amber-800">
                                    <h3 className="font-semibold mb-3 flex items-center gap-2 text-amber-800 dark:text-amber-200">
                                        <Clock className="h-4 w-4" />
                                        История решений ({selectedAppeal.resolution_history.length})
                                    </h3>
                                    <div className="space-y-3 max-h-60 overflow-y-auto">
                                        {selectedAppeal.resolution_history.map((historyItem, index) => (
                                            <div key={index} className="bg-white dark:bg-slate-800 p-3 rounded border text-sm">
                                                <div className="flex justify-between items-start mb-1">
                                                    <span className="font-medium">
                                                        {historyItem.resolution_type === 'favor_merchant' ? 'В пользу мерчанта' : 'В пользу трейдера'}
                                                    </span>
                                                    <Badge variant="outline" className="text-xs">
                                                        {historyItem.status}
                                                    </Badge>
                                                </div>
                                                <p className="text-slate-600 dark:text-slate-300 text-xs mb-1">{historyItem.resolution_reason}</p>

                                                {(historyItem.trader_response_description || historyItem.trader_requested_amount != null) && (
                                                    <div className="mt-2 p-2 bg-blue-50 dark:bg-blue-950 rounded border border-blue-100 dark:border-blue-800">
                                                        <div className="text-xs text-blue-700 dark:text-blue-300 font-medium mb-1 flex items-center gap-1">
                                                            <Users className="h-3 w-3" /> Ответ трейдера:
                                                        </div>
                                                        <TraderAppealResponse response={historyItem} currency={selectedAppeal.currency} />
                                                    </div>
                                                )}

                                                <div className="text-xs text-slate-400 mt-2">
                                                    <span>Решено: {formatResolvedBy(historyItem.resolved_by || '')} ({formatDate(historyItem.resolved_at)})</span>
                                                    {historyItem.reopened_by && (
                                                        <span className="block">Переоткрыто: {historyItem.reopened_by} ({formatDate(historyItem.reopened_at)})</span>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Форма переоткрытия */}
                            {isReopenMode ? (
                                <div className="space-y-4">
                                    <div className="bg-purple-50 dark:bg-purple-950 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                                        <h3 className="font-semibold mb-2 flex items-center gap-2 text-purple-800 dark:text-purple-200">
                                            <RotateCcw className="h-4 w-4" />
                                            Переоткрытие апелляции
                                        </h3>
                                        <p className="text-sm text-purple-600 dark:text-purple-300 mb-4">
                                            При переоткрытии апелляции статус транзакции изменится на "disputed" и средства будут заморожены.
                                        </p>

                                        <div className="space-y-4">
                                            <div className="space-y-2">
                                                <Label htmlFor="reopenDescription">Причина переоткрытия</Label>
                                                <Textarea
                                                    id="reopenDescription"
                                                    placeholder="Опишите причину переоткрытия апелляции..."
                                                    value={reopenDescription}
                                                    onChange={(e) => setReopenDescription(e.target.value)}
                                                    rows={3}
                                                    className="resize-none"
                                                />
                                            </div>

                                            <div className="space-y-2">
                                                <Label htmlFor="reopenFile">Прикрепить файлы <span className="text-red-500">*</span></Label>
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        id="reopenFile"
                                                        type="file"
                                                        multiple
                                                        accept=".pdf,.jpg,.jpeg,.png"
                                                        onChange={handleFileChange}
                                                        className="flex-1"
                                                    />
                                                </div>
                                                <p className="text-xs text-slate-500 dark:text-slate-400">До 10 файлов PDF/JPG/PNG, каждый до 10 МБ</p>
                                                {reopenFiles.map((file, index) => (
                                                    <div key={`${file.name}-${file.lastModified}-${index}`} className="flex items-center justify-between rounded border bg-white dark:bg-slate-800 p-2 text-sm">
                                                        <span className="truncate">{file.name}</span>
                                                        <Button type="button" variant="ghost" size="sm" onClick={() => setReopenFiles(reopenFiles.filter((_, i) => i !== index))}>
                                                            <X className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                <>
                                    {appealRecalculation && selectedAppeal ? <SupportRecalculationPanel
                                        key={`${supportIdentity}:${selectedAppeal.id}`} user={supportIdentity}
                                        transactionId={selectedAppeal.transaction_id} appealId={selectedAppeal.id}
                                        onCompleted={refreshAfterRecalculation} /> : <>
                                    {/* Стандартная форма решения */}
                                    <div className="space-y-3">
                                        <Label className="text-base font-semibold">
                                            {selectedAppeal?.externally_managed ? `Решение ${getExternalProviderName(selectedAppeal)}` : isReadOnly ? 'Принятое решение' : 'В чью пользу решение?'}
                                        </Label>
                                        <div className={isReadOnly ? "pointer-events-none opacity-60" : ""}>
                                            <RadioGroup
                                                value={resolution || ""}
                                                onValueChange={(v: string) => !isReadOnly && setResolution(v as AppealResolution)}
                                            >
                                                {!isNorosAppeal && <div
                                                    className={`flex items-start space-x-2 p-3 border rounded-md transition-colors ${resolution === AppealResolution.FAVOR_MERCHANT ? 'bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800' : isReadOnly ? 'bg-gray-50 dark:bg-slate-900 opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer'}`}
                                                    onClick={() => !isReadOnly && setResolution(AppealResolution.FAVOR_MERCHANT)}
                                                >
                                                    <RadioGroupItem value={AppealResolution.FAVOR_MERCHANT} id="favor_merchant" className="mt-1" />
                                                    <div className="grid gap-1.5 leading-none">
                                                        <Label htmlFor="favor_merchant" className={`font-semibold ${!isReadOnly && 'cursor-pointer'}`}>В пользу МЕРЧАНТА</Label>
                                                        <p className="text-sm text-muted-foreground">
                                                            {isWithdrawAppeal
                                                                ? 'Вывод будет отменен (cancelled). Средства в RUB вернутся мерчанту, а необходимая сумма будет удержана с баланса/страховки трейдера.'
                                                                : 'Транзакция будет завершена (completed). Средства зачислятся мерчанту (RUB) и спишутся у трейдера (USDT).'}
                                                        </p>
                                                    </div>
                                                </div>}
                                                <div
                                                    className={`flex items-start space-x-2 p-3 border rounded-md transition-colors ${resolution === AppealResolution.FAVOR_TRADER ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800' : isReadOnly ? 'bg-gray-50 dark:bg-slate-900 opacity-50' : 'hover:bg-slate-50 dark:hover:bg-slate-900 cursor-pointer'}`}
                                                    onClick={() => !isReadOnly && setResolution(AppealResolution.FAVOR_TRADER)}
                                                >
                                                    <RadioGroupItem value={AppealResolution.FAVOR_TRADER} id="favor_trader" className="mt-1" />
                                                    <div className="grid gap-1.5 leading-none">
                                                        <Label htmlFor="favor_trader" className={`font-semibold ${!isReadOnly && 'cursor-pointer'}`}>
                                                            {isNorosAppeal ? 'Отклонить апелляцию — оставить платёж отменённым' : selectedAppeal?.externally_managed ? `В пользу ${getExternalProviderName(selectedAppeal).toUpperCase()}` : 'В пользу ТРЕЙДЕРА'}
                                                        </Label>
                                                        <p className="text-sm text-muted-foreground">
                                                            {isNorosAppeal
                                                                ? 'Используйте после отказа NOROS в чате. Зачисления не будет; апелляция закроется. Успех подтверждается коллбэком NOROS.'
                                                                : selectedAppeal?.externally_managed
                                                                ? `Транзакция отменена по итоговому решению ${getExternalProviderName(selectedAppeal)}.`
                                                                : isWithdrawAppeal
                                                                    ? 'Вывод будет завершен (completed). Трейдер сохранит/получит свои USDT и комиссию, а списанные RUB останутся у мерчанта списанными.'
                                                                    : 'Транзакция будет отменена (cancelled). Средства останутся у трейдера.'}
                                                        </p>
                                                    </div>
                                                </div>
                                            </RadioGroup>
                                        </div>
                                    </div>

                                    <div className="space-y-2">
                                        <Label htmlFor="reason">Причина решения</Label>
                                        <Textarea
                                            id="reason"
                                            placeholder="Опишите причину (например: 'Чек поддельный', 'Деньги поступили на счет')"
                                            value={reason}
                                            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setReason(e.target.value)}
                                            rows={4}
                                            className="resize-none"
                                            readOnly={isReadOnly}
                                            disabled={isReadOnly}
                                        />
                                    </div>

                                    {isReadOnly && selectedAppeal?.resolution_method === 'recalculation' && (
                                        <div className="text-sm text-slate-500 dark:text-slate-400 border-t pt-4">
                                            <p className="break-all">Завершено с перерасчётом · {selectedAppeal.recalculation_id}</p>
                                        </div>
                                    )}
                                    </>}
                                </>
                            )}
                        </div>
                    </div>

                    <DialogFooter className="gap-3 border-t pt-4 sm:justify-between sm:space-x-0">
                        <div className="flex gap-2">
                            <Button variant="ghost" onClick={handleCloseModal} disabled={isSubmitting}>
                                Закрыть
                            </Button>
                        </div>

                        <div className="flex flex-wrap gap-2">
                            {/* Кнопка переоткрытия для закрытых апелляций */}
                            {!isReopenMode && canReopenAppeal(selectedAppeal) && (
                                <Button
                                    variant="outline"
                                    onClick={() => selectedAppeal && handleReopenClick(selectedAppeal)}
                                    className="border-purple-500 text-purple-600 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-950"
                                >
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Переоткрыть
                                </Button>
                            )}

                            {/* Кнопка подтверждения переоткрытия */}
                            {isReopenMode && (
                                <Button
                                    onClick={handleSubmitReopen}
                                    disabled={isSubmitting || reopenFiles.length === 0}
                                    className="bg-purple-600 hover:bg-purple-700"
                                >
                                    {isSubmitting ? 'Обработка...' : 'Подтвердить переоткрытие'}
                                </Button>
                            )}

                            {/* Кнопка решения спора */}
                            {!isReadOnly && !isReopenMode && !appealRecalculation && (
                                <Button onClick={() => setConfirmResolution(true)} disabled={isSubmitting || !reason.trim() || !resolution}>
                                    {isSubmitting ? 'Обработка...' : 'Проверить решение'}
                                </Button>
                            )}
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
            <Dialog open={confirmResolution && isModalOpen && !isReadOnly} onOpenChange={(open) => { if (!isSubmitting) setConfirmResolution(open); }}>
                <DialogContent className="max-w-xl">
                    <DialogHeader><DialogTitle>Подтвердите решение спора</DialogTitle></DialogHeader>
                    <div className="space-y-3 my-4">
                        <p className="break-all">Апелляция: {selectedAppeal?.id}</p>
                        <p className="break-all">Сделка: {selectedAppeal?.transaction_id}</p>
                        <p>{isWithdrawAppeal ? 'Выплата' : 'Входящий платёж'}: <strong>{selectedAppeal?.amount} {selectedAppeal?.currency}</strong></p>
                        <p>Решение: <strong>{resolution === AppealResolution.FAVOR_MERCHANT ? 'В пользу мерчанта' : isNorosAppeal ? 'Отклонить апелляцию NOROS' : 'В пользу трейдера'}</strong></p>
                        <p>Итоговый статус: <strong>{(isWithdrawAppeal ? resolution === AppealResolution.FAVOR_TRADER : resolution === AppealResolution.FAVOR_MERCHANT) ? 'Успешная (completed)' : 'Отменённая (cancelled)'}</strong></p>
                        <p>{isNorosAppeal ? 'Платёж останется отменённым, зачисления не будет.' : isWithdrawAppeal
                            ? resolution === AppealResolution.FAVOR_MERCHANT ? 'Выплата отменяется: средства возвращаются мерчанту, необходимые средства удерживаются у трейдера по правилам расчёта спора.' : 'Выплата подтверждается: трейдер сохраняет или получает начисление и комиссию, средства мерчанта остаются списанными.'
                            : resolution === AppealResolution.FAVOR_MERCHANT ? 'Платёж подтверждается: средства зачисляются мерчанту и списываются у трейдера с учётом уже проведённых расчётов.' : 'Платёж отменяется: резерв этой сделки освобождается, расчёты выполняются по правилам отмены.'}</p>
                        <p className="whitespace-pre-wrap">Причина: {reason}</p>
                        {resolutionError && <p role="alert" className="text-red-600">{resolutionError}</p>}
                    </div>
                    <DialogFooter>
                        <Button variant="outline" disabled={isSubmitting} onClick={() => setConfirmResolution(false)}>Назад</Button>
                        <Button disabled={isSubmitting} onClick={handleSubmitResolution}>{isSubmitting ? 'Обработка...' : 'Подтвердить и вынести решение'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
